// ======================================================================
// FILE: src/modules/wms/services/wmsCalendarService.js
// MODULE: Work Management System (WMS)
// FEATURE: Calendar Management API Service
//
// PURPOSE:
//   Frontend API client for Calendar Management
//
// BACKEND:
//   /api/wms/calendars
// ======================================================================

import axiosClient from "../../../api/axiosClient";

/**
 * ----------------------------------------------------------------------
 * LIST CALENDARS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/calendars
 */
async function listCalendars(params = {}) {

  const res = await axiosClient.get(
    "/api/wms/calendars",
    { params }
  );

  const payload = res.data?.data || {};

  return {
    items: payload.data || [],
    pagination: payload.pagination || {},
  };
}

/**
 * ----------------------------------------------------------------------
 * GET CALENDAR
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/calendars/:id
 */
async function getCalendar(calendarId) {

  const res = await axiosClient.get(
    `/api/wms/calendars/${calendarId}`
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * CREATE CALENDAR
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/calendars
 */
async function createCalendar(payload) {

  const res = await axiosClient.post(
    "/api/wms/calendars",
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE CALENDAR
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/calendars/:id
 */
async function updateCalendar(
  calendarId,
  payload
) {

  const res = await axiosClient.put(
    `/api/wms/calendars/${calendarId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE CALENDAR
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/calendars/:id
 */
async function deleteCalendar(calendarId) {

  const res = await axiosClient.delete(
    `/api/wms/calendars/${calendarId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * GET WORKDAYS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/calendars/:id/workdays
 */
async function getWorkdays(calendarId) {

  const res = await axiosClient.get(
    `/api/wms/calendars/${calendarId}/workdays`
  );

  return res.data.data || [];
}

/**
 * ----------------------------------------------------------------------
 * SAVE WORKDAYS
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/calendars/:id/workdays
 */
async function saveWorkdays(
  calendarId,
  payload
) {

  const res = await axiosClient.put(
    `/api/wms/calendars/${calendarId}/workdays`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * GET SHIFTS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/calendars/:id/shifts
 */
async function getShifts(calendarId) {

  const res = await axiosClient.get(
    `/api/wms/calendars/${calendarId}/shifts`
  );

  return res.data.data || [];
}

/**
 * ----------------------------------------------------------------------
 * CREATE SHIFT
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/calendars/:id/shifts
 */
async function createShift(
  calendarId,
  payload
) {

  const res = await axiosClient.post(
    `/api/wms/calendars/${calendarId}/shifts`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE SHIFT
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/calendars/:id/shifts/:shiftId
 */
async function updateShift(
  calendarId,
  shiftId,
  payload
) {

  const res = await axiosClient.put(
    `/api/wms/calendars/${calendarId}/shifts/${shiftId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE SHIFT
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/calendars/:id/shifts/:shiftId
 */
async function deleteShift(
  calendarId,
  shiftId
) {

  const res = await axiosClient.delete(
    `/api/wms/calendars/${calendarId}/shifts/${shiftId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * GET HOLIDAYS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/calendars/:id/holidays
 */
async function getHolidays(calendarId) {

  const res = await axiosClient.get(
    `/api/wms/calendars/${calendarId}/holidays`
  );

  return res.data.data || [];
}

/**
 * ----------------------------------------------------------------------
 * CREATE HOLIDAY
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/calendars/:id/holidays
 */
async function createHoliday(
  calendarId,
  payload
) {

  const res = await axiosClient.post(
    `/api/wms/calendars/${calendarId}/holidays`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE HOLIDAY
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/calendars/:id/holidays/:holidayId
 */
async function updateHoliday(
  calendarId,
  holidayId,
  payload
) {

  const res = await axiosClient.put(
    `/api/wms/calendars/${calendarId}/holidays/${holidayId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE HOLIDAY
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/calendars/:id/holidays/:holidayId
 */
async function deleteHoliday(
  calendarId,
  holidayId
) {

  const res = await axiosClient.delete(
    `/api/wms/calendars/${calendarId}/holidays/${holidayId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * EXPORT
 * ----------------------------------------------------------------------
 */
const wmsCalendarService = {

  listCalendars,
  getCalendar,

  createCalendar,
  updateCalendar,
  deleteCalendar,

  getWorkdays,
  saveWorkdays,

  getShifts,
  createShift,
  updateShift,
  deleteShift,

  getHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
};

export default wmsCalendarService;